import { useMutation } from '@apollo/client'
import * as Sentry from '@sentry/nextjs'
import { IconCircleCheck, IconPlus } from '@tabler/icons-react'
import { Form, message } from 'antd'
import moment from 'moment'
import styled from 'styled-components'

import { Preview } from '../templates/templateEditor/SidePreview'

import { DEFECT_STATUSES, DEFECT_TYPES } from './constants'
import { DefectModal, Field, FormFieldValues } from './DefectModal'
import { CREATE_DEFECT, GET_DEFECTS } from './graphQL'

import { convertToAssigneeInput } from '@/components/form-components/AssigneeSelect'
import { ProjectDropdown } from '@/components/form-components/dropdowns/ProjectDropdown'
import { Button } from '@/components/shared-components/BUI/Button'
import { useProject } from '@/context/projectContext'
import { useBreakpoint } from '@/hooks/useBreakpoint'

import {
  CreateDefectUiMutation,
  CreateDefectUiMutationVariables,
  DefectStatus,
} from 'types/api.graphql'

const FooterWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-direction: column;
  flex-flow: column-reverse;
  gap: 0.75rem;

  ${({ theme: { media } }) => media.sm`
    flex-direction: row;
    flex-flow: row;
  `}
`

type Props = {
  createType: string
  closeModal: () => void
  pendingCount?: number
  templateResponseId?: string
  templateResponseBlockIdentifier?: string
  onCreate?: (values: CreateDefectUiMutation['createDefect']) => void
  previewMode?: boolean
}

export const CreateDefectModal: React.FC<Props> = ({
  createType,
  closeModal,
  pendingCount,
  templateResponseId,
  templateResponseBlockIdentifier,
  onCreate,
  previewMode,
}) => {
  const [form] = Form.useForm()
  const { project } = useProject()

  const isMobile = useBreakpoint('sm')

  const [createDefect, { loading: isLoading }] = useMutation<
    CreateDefectUiMutation,
    CreateDefectUiMutationVariables
  >(CREATE_DEFECT, {
    awaitRefetchQueries: true,
    refetchQueries: [GET_DEFECTS],
  })

  const submitAndContinue = () => {
    form.setFieldValue(Field.CONTINUE, true)
    form.submit()
  }

  const submitAndDone = () => {
    form.setFieldValue(Field.CONTINUE, false)
    form.submit()
  }

  const onSubmit = async (values: FormFieldValues) => {
    try {
      const defect = await createDefect({
        variables: {
          input: {
            projectId: values.projectId,
            title: values.title,
            description: values.description,
            type: values.type,
            priority: values.priority,
            status: values.status,
            assignees: convertToAssigneeInput(values.assignees),
            dueDate: moment(values.dueDate).toISOString(),
            locationId: values.location,
            attachmentIds: values.attachments
              ?.map((a) => a.attachmentId)
              ?.filter(Boolean),
            ...(templateResponseId ? { templateResponseId } : {}),
            ...(templateResponseBlockIdentifier
              ? { templateResponseBlockIdentifier }
              : {}),
          },
        },
      })

      if (defect.errors) {
        throw new Error('Error creating defect')
      }

      message.success('Defect successfully created')

      onCreate?.(defect.data.createDefect)

      if (values.continue) {
        form.resetFields([
          Field.TITLE,
          Field.DESCRIPTION,
          Field.ATTACHMENTS,
          Field.TRANSCRIPTIONS,
        ])
      } else {
        form.resetFields()
        closeModal()
      }
    } catch (error) {
      // Swallow error so user can try resolve
      message.error(
        'Error creating defect. Please check your attachments and user permissions.'
      )

      Sentry.captureException(error)
    }
  }

  return (
    <DefectModal
      form={form}
      initialValues={{
        [Field.PROJECT_ID]: project?.id,
        [Field.TYPE]: createType || Object.keys(DEFECT_TYPES)[0],
        [Field.STATUS]: Object.keys(DEFECT_STATUSES)[0] as DefectStatus,
      }}
      open={!!createType}
      closeModal={closeModal}
      contextualDate={new Date().toISOString()}
      onSubmit={onSubmit}
      pendingCount={pendingCount}
      title={
        previewMode ? (
          <Preview>Preview only</Preview>
        ) : (
          <Form.Item noStyle name={Field.PROJECT_ID}>
            <ProjectDropdown />
          </Form.Item>
        )
      }
      footer={
        <FooterWrapper>
          <Button
            icon={<IconPlus size={16} />}
            onClick={previewMode ? closeModal : submitAndContinue}
            loading={isLoading}
            block={isMobile}
          >
            Save &amp; add more
          </Button>
          <Button
            variant="primary"
            icon={<IconCircleCheck size={16} />}
            onClick={previewMode ? closeModal : submitAndDone}
            loading={isLoading}
            block={isMobile}
          >
            Save &amp; finish
          </Button>
        </FooterWrapper>
      }
    />
  )
}
